import SearchableSelect from './search/SearchableSelect';
import CollectionSearch from './search/CollectionSearch';
import { filterRecords } from '../utils/search';
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Paper,
  FormControl,
  InputLabel,
  MenuItem,
  TextField,
  Alert,
  Divider,
  Stack
} from '@mui/material';
import { getCategoryTotals, getCategories, executeSimpleTransferWithAutoCategory, executeDetailedTransfer } from '../services/api';

function AccountMigrationModal({ open, onClose, accounts, onMigrationComplete }) {
  const [sourceAccount, setSourceAccount] = useState('');
  const [targetAccount, setTargetAccount] = useState('');
  const [sourceCategoryTotals, setSourceCategoryTotals] = useState([]);
  const [targetCategories, setTargetCategories] = useState([]);
  const [targetSearch, setTargetSearch] = useState('');
  const visibleTargets = filterRecords(targetCategories, targetSearch, category => [category.name], { rank: true });
  const [allocations, setAllocations] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [transferMode, setTransferMode] = useState('simple'); // 'simple' or 'detailed'

  // Get active accounts only
  const activeAccounts = accounts.filter(a => a.account_status !== 'archived');

  // Calculate totals
  const sourceTotal = sourceCategoryTotals.reduce((sum, cat) => sum + parseFloat(cat.total_amount || 0), 0);
  const allocatedTotal = Object.values(allocations).reduce((sum, amount) => sum + parseFloat(amount || 0), 0);
  const remaining = sourceTotal - allocatedTotal;
  
  // Validation logic for both modes
  const canMigrate = sourceAccount && targetAccount && sourceTotal !== 0 && (
    transferMode === 'simple' || 
    (transferMode === 'detailed' && Math.abs(remaining) < 0.01)
  );

  // Fetch source account category totals
  useEffect(() => {
    if (sourceAccount) {
      fetchSourceCategoryTotals();
    } else {
      setSourceCategoryTotals([]);
    }
  }, [sourceAccount]);

  // Get target categories when target account changes
  useEffect(() => {
    if (targetAccount) {
      fetchTargetCategories();
    } else {
      setTargetCategories([]);
    }
    setAllocations({});
  }, [targetAccount]);

  const fetchTargetCategories = async () => {
    try {
      const response = await getCategories();
      const accountCategories = response.data.filter(
        cat => cat.account_id === parseInt(targetAccount) && !cat.is_deleted
      );
      setTargetCategories(accountCategories);
    } catch (err) {
      console.error('Failed to fetch target categories:', err);
      setError('Failed to load target account categories');
    }
  };

  const fetchSourceCategoryTotals = async () => {
    try {
      setLoading(true);
      const response = await getCategoryTotals(sourceAccount);
      setSourceCategoryTotals(response.data);
    } catch (err) {
      console.error('Failed to fetch category totals:', err);
      setError('Failed to load source account categories');
    } finally {
      setLoading(false);
    }
  };

  const handleAllocationChange = (categoryId, value) => {
    const numValue = parseFloat(value) || 0;
    setAllocations(prev => ({
      ...prev,
      [categoryId]: numValue
    }));
  };

  const handleMigrate = async () => {
    try {
      setLoading(true);
      setError('');
      
      let response;
      
      if (transferMode === 'simple') {
        // Execute simple transfer with auto-category creation
        response = await executeSimpleTransferWithAutoCategory(
          parseInt(sourceAccount),
          parseInt(targetAccount)
        );
        
        // Show success message for simple transfer
        const autoCategory = response.data.auto_category;
        const categoryMessage = autoCategory.was_created 
          ? `Created new category: "${autoCategory.name}"`
          : `Used existing category: "${autoCategory.name}"`;
        
        alert(`Simple transfer completed successfully!\n\nTransferred ${response.data.source_account.transferred_amount} from ${response.data.source_account.name} to ${response.data.destination_account.name}.\n\n${categoryMessage}`);
        
      } else {
        // Execute detailed transfer with category allocations
        response = await executeDetailedTransfer(
          parseInt(sourceAccount),
          parseInt(targetAccount),
          allocations
        );
        
        // Show success message for detailed transfer
        const allocationsText = response.data.allocations
          .map(alloc => `  • ${alloc.category_name}: $${alloc.amount}`)
          .join('\n');
        
        alert(`Detailed transfer completed successfully!\n\nTransferred ${response.data.source_account.transferred_amount} from ${response.data.source_account.name} to ${response.data.destination_account.name}.\n\nAllocations:\n${allocationsText}`);
      }
      
      // Refresh data and close modal
      onMigrationComplete?.();
      handleClose();
      
    } catch (err) {
      console.error('Migration failed:', err);
      const errorMsg = err.response?.data?.msg || 'Migration failed. Please try again.';
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSourceAccount('');
    setTargetAccount('');
    setSourceCategoryTotals([]);
    setTargetCategories([]);
    setAllocations({});
    setError('');
    setTransferMode('simple');
    onClose();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="lg" 
      fullWidth
      PaperProps={{
        sx: { minHeight: '70vh' }
      }}
    >
      <DialogTitle>
        <Typography variant="h5" component="div">
          🔄 Account Migration Tool - Pattern 1
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Transfer all balances from one account to another
        </Typography>
      </DialogTitle>
      
      <DialogContent sx={{ p: 3 }}>
        {/* Transfer Mode Selection */}
        <Box sx={{ mb: 4, p: 2, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
          <Typography variant="h6" gutterBottom>
            Choose Transfer Mode
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Button
                variant={transferMode === 'simple' ? 'contained' : 'outlined'}
                fullWidth
                onClick={() => {
                  setTransferMode('simple');
                  setAllocations({});
                }}
                size="large"
                sx={{ p: 2, flexDirection: 'column', height: 80 }}
              >
                <Typography variant="h6">🚀 Simple Transfer</Typography>
                <Typography variant="caption">Quick migration with auto-category</Typography>
              </Button>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Button
                variant={transferMode === 'detailed' ? 'contained' : 'outlined'}
                fullWidth
                onClick={() => {
                  setTransferMode('detailed');
                  setAllocations({});
                }}
                size="large"
                sx={{ p: 2, flexDirection: 'column', height: 80 }}
              >
                <Typography variant="h6">🎯 Detailed Transfer</Typography>
                <Typography variant="caption">Manual allocation to categories</Typography>
              </Button>
            </Grid>
          </Grid>
          
          {transferMode === 'simple' && (
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Simple Mode:</strong> Creates a single category "{sourceAccount ? activeAccounts.find(a => a.id === parseInt(sourceAccount))?.name || 'SourceAccount' : 'SourceAccount'}-to-{targetAccount ? activeAccounts.find(a => a.id === parseInt(targetAccount))?.name || 'TargetAccount' : 'TargetAccount'} Full Transfer" with the total amount.
              </Typography>
            </Alert>
          )}
          
          {transferMode === 'detailed' && (
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Detailed Mode:</strong> Manually allocate specific amounts to target categories. Migration enabled when all money is allocated.
              </Typography>
            </Alert>
          )}
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Account Selection */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth sx={{ minWidth: 300 }}>
              <InputLabel>Source Account (Transfer FROM)</InputLabel>
              <SearchableSelect
                value={sourceAccount}
                onChange={(e) => setSourceAccount(e.target.value)}
                label="Source Account (Transfer FROM)"
                sx={{ minWidth: 300 }}
              >
                {activeAccounts.map((account) => (
                  <MenuItem key={account.id} value={account.id}>
                    {account.name}
                  </MenuItem>
                ))}
              </SearchableSelect>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth sx={{ minWidth: 300 }}>
              <InputLabel>Target Account (Transfer TO)</InputLabel>
              <SearchableSelect
                value={targetAccount}
                onChange={(e) => setTargetAccount(e.target.value)}
                label="Target Account (Transfer TO)"
                disabled={!sourceAccount}
                sx={{ minWidth: 300 }}
              >
                {activeAccounts
                  .filter(a => a.id !== parseInt(sourceAccount))
                  .map((account) => (
                    <MenuItem key={account.id} value={account.id}>
                      {account.name}
                    </MenuItem>
                  ))}
              </SearchableSelect>
            </FormControl>
          </Grid>
        </Grid>

        {sourceAccount && targetAccount && (
          <>
            <Grid container spacing={3}>
              {/* Source Account Categories - READ ONLY */}
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, backgroundColor: '#f8f9fa' }}>
                  <Typography variant="h6" gutterBottom color="primary">
                    📊 {activeAccounts.find(a => a.id === parseInt(sourceAccount))?.name} Current Balances
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                    (Read-only reference)
                  </Typography>
                  
                  {loading ? (
                    <Typography>Loading categories...</Typography>
                  ) : (
                    <Stack spacing={1}>
                      {sourceCategoryTotals.map((category, index) => (
                        <Box
                          key={index}
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            p: 1,
                            backgroundColor: 'white',
                            borderRadius: 1,
                            border: '1px solid #e0e0e0'
                          }}
                        >
                          <Typography variant="body2">
                            {category.category_name}
                          </Typography>
                          <Typography 
                            variant="body2" 
                            fontWeight="bold"
                            color={parseFloat(category.total_amount) >= 0 ? 'success.main' : 'error.main'}
                          >
                            {formatCurrency(category.total_amount)}
                          </Typography>
                        </Box>
                      ))}
                      
                      <Divider sx={{ my: 1 }} />
                      
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          p: 1.5,
                          backgroundColor: '#e3f2fd',
                          borderRadius: 1,
                          border: '2px solid #1976d2'
                        }}
                      >
                        <Typography variant="h6" color="primary">
                          TOTAL:
                        </Typography>
                        <Typography variant="h6" fontWeight="bold" color="primary">
                          {formatCurrency(sourceTotal)}
                        </Typography>
                      </Box>
                    </Stack>
                  )}
                </Paper>
              </Grid>

              {/* Target Account - Mode-based Display */}
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, backgroundColor: '#f0f8f0' }}>
                  <Typography variant="h6" gutterBottom color="success.main">
                    🎯 Transfer to {activeAccounts.find(a => a.id === parseInt(targetAccount))?.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                    {transferMode === 'simple' ? '(Complete balance will be transferred)' : '(Allocate amounts to categories)'}
                  </Typography>
                  
                  <CollectionSearch label="Search migration categories" value={targetSearch} onChange={setTargetSearch}
                    total={targetCategories.length} shown={visibleTargets.length} />
                  {targetSearch && !visibleTargets.length && <Typography>No matching categories.</Typography>}
                  {transferMode === 'simple' ? (
                    <Stack spacing={2}>
                      <Alert severity="info">
                        <Typography variant="body2">
                          <strong>Simple Transfer Mode:</strong><br/>
                          All {formatCurrency(sourceTotal)} will be transferred to a new category called:<br/>
                          <em>"{activeAccounts.find(a => a.id === parseInt(sourceAccount))?.name || 'Source'}-to-{activeAccounts.find(a => a.id === parseInt(targetAccount))?.name || 'Target'} Full Transfer"</em>
                        </Typography>
                      </Alert>
                      
                      <Typography variant="body2" sx={{ mt: 2 }}>
                        <strong>Available Categories in Target Account:</strong>
                      </Typography>
                      
                      <Stack spacing={1}>
                        {targetCategories.length > 0 ? (
                          visibleTargets.map((category) => (
                            <Box
                              key={category.id}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                p: 1,
                                backgroundColor: 'white',
                                borderRadius: 1,
                                border: '1px solid #e0e0e0'
                              }}
                            >
                              <Typography variant="body2">
                                • {category.name}
                              </Typography>
                            </Box>
                          ))
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Loading categories...
                          </Typography>
                        )}
                      </Stack>
                    </Stack>
                  ) : (
                    <Stack spacing={2}>
                      <Typography variant="body2" sx={{ mb: 2 }}>
                        <strong>Allocate {formatCurrency(sourceTotal)} to Categories:</strong>
                      </Typography>
                      
                      {targetCategories.length > 0 ? (
                        visibleTargets.map((category) => (
                          <Box key={category.id}>
                            <Typography variant="body2" sx={{ mb: 0.5 }}>
                              {category.name}
                            </Typography>
                            <TextField
                              fullWidth
                              type="number"
                              inputProps={{
                                step: "0.01",
                                min: "0"
                              }}
                              value={allocations[category.id] || ''}
                              onChange={(e) => handleAllocationChange(category.id, e.target.value)}
                              placeholder="0.00"
                              size="small"
                            />
                          </Box>
                        ))
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          Loading categories...
                        </Typography>
                      )}
                      
                      <Divider sx={{ my: 2 }} />
                      
                      <Box
                        sx={{
                          p: 1.5,
                          backgroundColor: '#e8f5e8',
                          borderRadius: 1,
                          border: '2px solid #4caf50'
                        }}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="body2">
                            ALLOCATED:
                          </Typography>
                          <Typography variant="body2" fontWeight="bold">
                            {formatCurrency(allocatedTotal)}
                          </Typography>
                        </Box>
                      </Box>
                    </Stack>
                  )}
                </Paper>
              </Grid>
            </Grid>

            {/* Migration Status */}
            <Box sx={{ mt: 3 }}>
              <Paper sx={{ 
                p: 2, 
                backgroundColor: canMigrate ? '#e8f5e8' : '#fff3e0',
                border: `2px solid ${canMigrate ? '#4caf50' : '#ff9800'}`
              }}>
                <Typography variant="h6" gutterBottom>
                  💰 Transfer Summary
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2">Amount to Transfer:</Typography>
                    <Typography variant="h6" color="primary">
                      {formatCurrency(sourceTotal)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2">Transfer Mode:</Typography>
                    <Typography variant="h6" color="success.main">
                      {transferMode === 'simple' ? 'Simple' : 'Detailed'}
                    </Typography>
                  </Grid>
                  {transferMode === 'detailed' && (
                    <Grid item xs={12} sm={4}>
                      <Typography variant="body2">REMAINING:</Typography>
                      <Typography 
                        variant="h6" 
                        fontWeight="bold"
                        color={Math.abs(remaining) < 0.01 ? 'success.main' : 'error.main'}
                      >
                        {formatCurrency(remaining)}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
                
                {/* Success messages */}
                {canMigrate && sourceTotal !== 0 && transferMode === 'simple' && (
                  <Alert severity="success" sx={{ mt: 2 }}>
                    ✅ Ready to transfer! {formatCurrency(sourceTotal)} will be moved to a new category "{activeAccounts.find(a => a.id === parseInt(sourceAccount))?.name}-to-{activeAccounts.find(a => a.id === parseInt(targetAccount))?.name} Full Transfer".
                  </Alert>
                )}
                
                {canMigrate && sourceTotal !== 0 && transferMode === 'detailed' && (
                  <Alert severity="success" sx={{ mt: 2 }}>
                    ✅ Ready to transfer! All {formatCurrency(sourceTotal)} has been allocated to target categories.
                  </Alert>
                )}
                
                {/* Warning/error messages */}
                {sourceTotal === 0 && sourceAccount && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    ⚠️ Source account has zero balance - nothing to transfer.
                  </Alert>
                )}
                
                {!sourceAccount || !targetAccount && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    ℹ️ Please select both source and target accounts to proceed.
                  </Alert>
                )}
                
                {transferMode === 'detailed' && remaining > 0.01 && sourceTotal > 0 && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    ⚠️ You still need to allocate {formatCurrency(remaining)} more to complete the transfer.
                  </Alert>
                )}
                
                {transferMode === 'detailed' && remaining < -0.01 && (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    ❌ Over-allocated by {formatCurrency(Math.abs(remaining))}. Please reduce allocations.
                  </Alert>
                )}
              </Paper>
            </Box>
          </>
        )}
      </DialogContent>
      
      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleMigrate}
          disabled={!canMigrate || loading}
          color="primary"
          size="large"
        >
          {loading ? 'Processing Transfer...' : `Execute ${transferMode === 'simple' ? 'Simple' : 'Detailed'} Transfer`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default AccountMigrationModal;
